import { useState, useCallback } from "react";
import { ServiceResult } from "../types/api";

/**
 * Wraps a service mutation in the loading/error bookkeeping every screen
 * needs, and unwraps ServiceResult into a plain value.
 *
 * Eight hooks each carried three copies of this same sixteen-line block -
 * about 24 in total - and one of them (useChallengeOperations) forgot
 * clearError.
 *
 * Usage:
 *   const ops = useEntityOperations();
 *   const createUser = (input: CreateUserInput) =>
 *     ops.run(() => UserService.createUser(input), "Failed to create user");
 */
export const useEntityOperations = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Returns the mutation's `data` on success and null on failure, setting
   * `error` from the service message where there is one.
   */
  const run = useCallback(
    async <T>(
      operation: () => Promise<ServiceResult<T>>,
      fallbackMessage: string
    ): Promise<T | null> => {
      setLoading(true);
      setError(null);

      try {
        const result = await operation();

        if (result.success) {
          return (result.data ?? null) as T | null;
        }

        setError(result.error || fallbackMessage);
        return null;
      } catch (err) {
        setError(fallbackMessage);
        return null;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /**
   * For deletes and other mutations whose caller only cares whether it
   * worked - `run` cannot express that, because a successful call with no
   * payload also returns null.
   */
  const runBoolean = useCallback(
    async (
      operation: () => Promise<ServiceResult<unknown>>,
      fallbackMessage: string
    ): Promise<boolean> => {
      setLoading(true);
      setError(null);

      try {
        const result = await operation();

        if (result.success) return true;

        setError(result.error || fallbackMessage);
        return false;
      } catch (err) {
        setError(fallbackMessage);
        return false;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const clearError = useCallback(() => setError(null), []);

  // setLoading/setError are exposed for the handful of mutations that cannot
  // use run(): usePostOperations' optimistic like toggles, and
  // useCommentOperations.updateComment, which rethrows rather than
  // returning null.
  return {
    loading,
    error,
    run,
    runBoolean,
    clearError,
    setError,
    setLoading,
  };
};
